import { useState } from 'react'
import { CalendarCheck, Plus, X } from 'lucide-react'
import adminApi from '../../../services/adminApi'

const STATUS_STYLES = {
  new: 'bg-yellow-500/20 text-yellow-300',
  contacted: 'bg-blue-500/20 text-blue-300',
  closed: 'bg-green-500/20 text-green-300',
}

const FORMAT_OPTIONS = [
  { value: 'online', label: 'Online' },
  { value: 'in-person', label: 'In-Person' },
  { value: 'hybrid', label: 'Hybrid' },
]

const INITIAL_FORM = {
  facilitatorProfileId: '',
  title: '',
  description: '',
  duration: '',
  format: 'online',
  fee: '',
  displayOrder: 0,
}

const FacilitatorConsultationsTab = ({ listings, interests, facilitators, onRefresh }) => {
  const [activeView, setActiveView] = useState('listings')
  const [showForm, setShowForm] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [form, setForm] = useState(INITIAL_FORM)
  const [imageFile, setImageFile] = useState(null)
  const [saving, setSaving] = useState(false)
  const [updatingId, setUpdatingId] = useState(null)

  const openCreate = () => {
    setForm(INITIAL_FORM)
    setImageFile(null)
    setEditingId(null)
    setShowForm(true)
  }

  const openEdit = (listing) => {
    setForm({
      facilitatorProfileId: listing.facilitatorProfileId || '',
      title: listing.title || '',
      description: listing.description || '',
      duration: listing.duration || '',
      format: listing.format || 'online',
      fee: listing.fee || '',
      displayOrder: listing.displayOrder || 0,
    })
    setImageFile(null)
    setEditingId(listing.id)
    setShowForm(true)
  }

  const handleSave = async (e) => {
    e.preventDefault()
    if (!form.title.trim() || (!editingId && !form.facilitatorProfileId)) return

    try {
      setSaving(true)
      const formData = new FormData()
      Object.entries(form).forEach(([key, val]) => {
        if (val !== '' && val !== null && val !== undefined) formData.append(key, val)
      })
      if (imageFile) formData.append('image', imageFile)

      if (editingId) {
        await adminApi.put(`/api/admin/facilitator-consultations/${editingId}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
      } else {
        await adminApi.post('/api/admin/facilitator-consultations', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
      }
      setShowForm(false)
      onRefresh?.()
    } catch (err) {
      console.error('Error saving consultation:', err)
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this listing?')) return
    try {
      await adminApi.delete(`/api/admin/facilitator-consultations/${id}`)
      onRefresh?.()
    } catch (err) {
      console.error('Error deleting:', err)
    }
  }

  const handleToggleActive = async (listing) => {
    try {
      const formData = new FormData()
      formData.append('active', !listing.active)
      await adminApi.put(`/api/admin/facilitator-consultations/${listing.id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      onRefresh?.()
    } catch (err) {
      console.error('Error toggling active:', err)
    }
  }

  const handleInterestStatus = async (id, status) => {
    try {
      setUpdatingId(id)
      await adminApi.patch(`/api/admin/facilitator-consultation-interests/${id}/status`, { status })
      onRefresh?.()
    } catch (err) {
      console.error('Error updating interest status:', err)
    } finally {
      setUpdatingId(null)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-4">
        <h3 className="text-xl font-semibold text-white flex items-center gap-2">
          <CalendarCheck className="w-6 h-6" />
          Facilitator Consultations
        </h3>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setActiveView('listings')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeView === 'listings' ? 'bg-purple-600 text-white' : 'bg-white/10 text-gray-300 hover:bg-white/20'
            }`}
          >
            Listings ({listings?.length || 0})
          </button>
          <button
            onClick={() => setActiveView('interests')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeView === 'interests' ? 'bg-purple-600 text-white' : 'bg-white/10 text-gray-300 hover:bg-white/20'
            }`}
          >
            Interests ({interests?.length || 0})
          </button>
        </div>
      </div>

      {/* LISTINGS VIEW */}
      {activeView === 'listings' && (
        <div>
          <div className="flex justify-end mb-4">
            <button
              onClick={openCreate}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Listing
            </button>
          </div>

          {!listings?.length ? (
            <div className="text-center py-16 text-gray-400">
              <CalendarCheck className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No consultation listings yet</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-white/10">
                <thead>
                  <tr>
                    {['Facilitator', 'Title', 'Format', 'Fee', 'Duration', 'Active', 'Interests', 'Created', 'Actions'].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {listings.map((listing) => (
                    <tr key={listing.id} className="hover:bg-white/5">
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-white">
                        {listing.facilitatorProfile?.user?.name || 'N/A'}
                      </td>
                      <td className="px-4 py-4 text-sm font-medium text-white max-w-[200px] truncate">
                        {listing.title}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-300">{listing.format}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-300">{listing.fee || '-'}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-300">{listing.duration || '-'}</td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <button
                          onClick={() => handleToggleActive(listing)}
                          className={`px-2.5 py-1 rounded-full text-xs font-medium ${listing.active ? 'bg-green-500/20 text-green-300' : 'bg-gray-500/20 text-gray-400'}`}
                        >
                          {listing.active ? 'Active' : 'Inactive'}
                        </button>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-300">{listing._count?.interests || 0}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-300">
                        {new Date(listing.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm">
                        <div className="flex items-center gap-2">
                          <button onClick={() => openEdit(listing)} className="text-blue-400 hover:text-blue-300">Edit</button>
                          <button onClick={() => handleDelete(listing.id)} className="text-red-400 hover:text-red-300">Delete</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* INTERESTS VIEW */}
      {activeView === 'interests' && (
        <div>
          {!interests?.length ? (
            <div className="text-center py-16 text-gray-400">
              <CalendarCheck className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No interest submissions yet</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-white/10">
                <thead>
                  <tr>
                    {['Listing', 'Facilitator', 'Name', 'Email', 'Phone', 'Message', 'Status', 'Submitted'].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {interests.map((interest) => (
                    <tr key={interest.id} className="hover:bg-white/5">
                      <td className="px-4 py-4 text-sm text-white max-w-[150px] truncate">
                        {interest.consultationListing?.title || 'N/A'}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-300">
                        {interest.consultationListing?.facilitatorProfile?.user?.name || 'N/A'}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-white">{interest.name}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-300">
                        <a href={`mailto:${interest.email}`} className="hover:text-white transition-colors">{interest.email}</a>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-300">
                        {interest.phone ? (
                          <a href={`tel:${interest.phone}`} className="hover:text-white transition-colors">{interest.phone}</a>
                        ) : '-'}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-300 max-w-[200px] truncate">
                        {interest.message || '-'}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <select
                          value={interest.status}
                          onChange={(e) => handleInterestStatus(interest.id, e.target.value)}
                          disabled={updatingId === interest.id}
                          className={`rounded-full px-3 py-1 text-xs font-medium border-0 cursor-pointer focus:outline-none focus:ring-2 focus:ring-purple-500 ${STATUS_STYLES[interest.status] || 'bg-gray-500/20 text-gray-300'}`}
                          style={{ backgroundColor: 'transparent' }}
                        >
                          <option value="new" className="bg-slate-800 text-white">New</option>
                          <option value="contacted" className="bg-slate-800 text-white">Contacted</option>
                          <option value="closed" className="bg-slate-800 text-white">Closed</option>
                        </select>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-300">
                        {new Date(interest.createdAt).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* CREATE/EDIT MODAL */}
      {showForm && (
        <div
          className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setShowForm(false) }}
        >
          <div className="bg-slate-800 w-full max-w-lg rounded-2xl max-h-[90vh] overflow-y-auto border border-white/20">
            <form onSubmit={handleSave} className="p-6 space-y-4">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-lg font-bold text-white">
                  {editingId ? 'Edit Listing' : 'New Listing'}
                </h2>
                <button type="button" onClick={() => setShowForm(false)} className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-full">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {!editingId && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Facilitator <span className="text-red-400">*</span>
                  </label>
                  <select
                    required
                    value={form.facilitatorProfileId}
                    onChange={(e) => setForm(prev => ({ ...prev, facilitatorProfileId: e.target.value }))}
                    className="w-full px-4 py-3 bg-slate-700 border border-white/20 text-white rounded-xl focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="">Select facilitator...</option>
                    {facilitators?.map(f => (
                      <option key={f.profileId} value={f.profileId}>{f.name || `ID ${f.profileId}`}</option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Title <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={form.title}
                  onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-4 py-3 bg-slate-700 border border-white/20 text-white rounded-xl focus:ring-2 focus:ring-purple-500"
                  placeholder="e.g., 1-on-1 Breath Assessment"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Description</label>
                <textarea
                  rows={3}
                  value={form.description}
                  onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-4 py-3 bg-slate-700 border border-white/20 text-white rounded-xl focus:ring-2 focus:ring-purple-500 resize-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Duration</label>
                  <input
                    type="text"
                    value={form.duration}
                    onChange={(e) => setForm(prev => ({ ...prev, duration: e.target.value }))}
                    className="w-full px-4 py-3 bg-slate-700 border border-white/20 text-white rounded-xl focus:ring-2 focus:ring-purple-500"
                    placeholder="60 mins"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Fee</label>
                  <input
                    type="text"
                    value={form.fee}
                    onChange={(e) => setForm(prev => ({ ...prev, fee: e.target.value }))}
                    className="w-full px-4 py-3 bg-slate-700 border border-white/20 text-white rounded-xl focus:ring-2 focus:ring-purple-500"
                    placeholder="Rs 1500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Format</label>
                <select
                  value={form.format}
                  onChange={(e) => setForm(prev => ({ ...prev, format: e.target.value }))}
                  className="w-full px-4 py-3 bg-slate-700 border border-white/20 text-white rounded-xl focus:ring-2 focus:ring-purple-500"
                >
                  {FORMAT_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Image</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setImageFile(e.target.files[0] || null)}
                  className="w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-purple-500/20 file:text-purple-300 hover:file:bg-purple-500/30"
                />
              </div>

              <button
                type="submit"
                disabled={saving}
                className="w-full py-3 bg-purple-600 text-white rounded-xl font-semibold hover:bg-purple-700 transition-colors disabled:opacity-50"
              >
                {saving ? 'Saving...' : editingId ? 'Update' : 'Create'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default FacilitatorConsultationsTab
